import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { EventCard3D } from "@/components/events/EventCard3D";

interface ImportedEvent {
  id: string;
  source_name: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  ticket_status: string | null;
  created_at?: string;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}

interface ImportedEventsByCategoryProps {
  isRTL: boolean;
  category: string;
  title: { ar: string; en: string };
  showViewAll?: boolean;
  limit?: number;
  viewAllLink?: string;
  icon?: LucideIcon;
  iconBg?: string;
}

const parseEventDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  
  const formats = [
    /(\d{4})-(\d{1,2})-(\d{1,2})/,
    /(\d{1,2})[-/](\d{1,2})[-/](\d{4})/,
    /(\d{2})-(\d{1,2})-(\d{1,2})/,
  ];
  
  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      let year, month, day;
      if (format === formats[0]) {
        [, year, month, day] = match;
      } else if (format === formats[1]) {
        [, day, month, year] = match;
      } else {
        [, year, month, day] = match;
        year = `20${year}`;
      }
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }
  }
  
  const directParse = new Date(dateStr);
  if (!isNaN(directParse.getTime())) return directParse;
  
  return null;
};

const calculateCountdown = (targetDate: Date): CountdownTime => {
  const now = new Date();
  const diff = targetDate.getTime() - now.getTime();
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isPast: true };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, isPast: false };
};

const useCountdown = (date: string | null) => {
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);
  
  useEffect(() => {
    const targetDate = parseEventDate(date);
    if (!targetDate) return;
    
    const updateCountdown = () => {
      setCountdown(calculateCountdown(targetDate));
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, [date]);
  
  return countdown;
};

// Wrapper component to handle countdown for each event
const EventCardWrapper = ({ event, isRTL }: { event: ImportedEvent; isRTL: boolean }) => {
  const countdown = useCountdown(event.date);
  return <EventCard3D event={event} isRTL={isRTL} countdown={countdown} />;
};

// Map categories to their dedicated pages
const categoryPageMap: { [key: string]: string } = {
  "seasons": "/saudi-seasons",
  "exhibitions": "/exhibitions",
  "festivals": "/saudi-festivals",
  "conferences-seminars": "/conferences-seminars",
  "public-occasions": "/public-occasions",
  "international-days": "/international-days",
  "x-spaces": "/x-spaces",
  "tiktok-lives": "/tiktok-lives"
};

export const ImportedEventsByCategory = ({ 
  isRTL, 
  category, 
  title, 
  showViewAll = true,
  limit = 6,
  viewAllLink,
  icon: Icon,
  iconBg = "bg-primary"
}: ImportedEventsByCategoryProps) => {
  const [events, setEvents] = useState<ImportedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, [category]);

  const fetchEvents = async () => {
    const { data, error } = await supabase
      .from("scraped_events")
      .select("*")
      .eq("is_published", true)
      .eq("category", category)
      .order("scraped_at", { ascending: false })
      .limit(limit);

    if (!error && data) {
      setEvents(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </section>
    );
  }

  if (events.length === 0) {
    return null;
  }

  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            {Icon && (
              <div className={`p-3.5 rounded-2xl ${iconBg} shadow-lg shadow-primary/20`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
            )}
            <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              {isRTL ? title.ar : title.en}
            </h2>
          </div>
          {showViewAll && (
            <Link to={viewAllLink || categoryPageMap[category] || `/${category}`}>
              <Button variant="outline" className="rounded-xl border-2 hover:bg-primary hover:text-white hover:border-primary transition-all">
                {isRTL ? "عرض الكل" : "View All"}
              </Button>
            </Link>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.map((event) => (
            <EventCardWrapper key={event.id} event={event} isRTL={isRTL} />
          ))}
        </div>
      </div>
    </section>
  );
};
